import { Head, router } from "@inertiajs/react";
import { useCallback, useEffect, useRef, useState } from "react";

import AppLogoIcon from "@/components/app-logo-icon";
import { FreeTrialBanner } from "@/components/free-trial-banner";
import { ApiKeyModal } from "@/components/onboarding/api-key-modal";
import { BrandingStep } from "@/components/onboarding/branding-step";
import { DomainStep } from "@/components/onboarding/domain-step";
import { Toaster } from "@/components/ui/sonner";
import { useFlash } from "@/hooks/use-flash";
import { continueOnboardingBrandingPath } from "@/routes";

type StepKey = "connect_domain" | "branding" | "api_key";

interface OnboardingStep {
  key: StepKey;
  status: "pending" | "completed" | "skipped";
  completedAt?: string;
}

interface DomainInfo {
  id: number;
  hostname: string;
  verificationStatus: "unverified" | "verified" | "pending" | "failed";
  verificationToken: string;
  verificationRecordName: string;
  verificationRecordValue: string;
  verificationHostname: string;
  verificationCheckedAt?: string | null;
  verifiedAt?: string | null;
  customLogoUrl?: string | null;
  bgColorHex?: string | null;
  textColorHex?: string | null;
}

interface DomainClaim {
  id: number;
  status: "pending" | "verified" | "failed" | "expired" | "canceled";
  hostname: string;
  recordName: string;
  recordValue: string;
  expiresAt?: string | null;
  checkedAt?: string | null;
  verifiedAt?: string | null;
}

interface LogoCandidate {
  url: string;
  source: string;
}

interface BrandingExtraction {
  id: number;
  status: "pending" | "processing" | "completed" | "failed";
  stage: string;
  logoCandidates: LogoCandidate[];
  suggestedBgColorHex?: string | null;
  suggestedTextColorHex?: string | null;
  draftBgColorHex?: string | null;
  draftTextColorHex?: string | null;
  draftLogoUrl?: string | null;
  previewImageUrl?: string | null;
  previewStatus?: string | null;
  errorMessage?: string | null;
}

interface ExistingApiKey {
  id: number;
  name: string;
  tokenPrefix: string;
}

interface Domain {
  id: number;
  hostname: string;
  verification_status?: string;
}

interface Props {
  domain: DomainInfo | null;
  claim: DomainClaim | null;
  extraction: BrandingExtraction | null;
  domains: Domain[];
  existingApiKey: ExistingApiKey | null;
  newApiKeyToken: string | null;
  onboarding: { steps: OnboardingStep[] };
  leadDomain?: string | null;
}

interface BrandingDraftPayload {
  bgColorHex: string;
  textColorHex: string;
  selectedLogoUrl: string | null;
  customLogo: File | null;
  ogTitle: string;
  ogDescription: string;
  heroImageUrl: string;
}

type OnboardingPhase = "domain" | "branding";

export default function OnboardingIndex({
  domain,
  claim,
  extraction,
  domains,
  existingApiKey,
  newApiKeyToken,
  onboarding,
  leadDomain,
}: Props) {
  useFlash();

  const domainStepStatus = onboarding.steps.find((s) => s.key === "connect_domain")?.status;
  const hasVerifiedDomain = domain !== null && domain.verificationStatus === "verified";
  const hasDomainConnected = hasVerifiedDomain || (domainStepStatus === "completed" && domain !== null);

  const initialPhase: OnboardingPhase = hasDomainConnected ? "branding" : "domain";

  const [phase, setPhase] = useState<OnboardingPhase>(initialPhase);
  const [transitioning, setTransitioning] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const prevPhaseRef = useRef(phase);

  useEffect(() => {
    if (hasDomainConnected && phase === "domain" && prevPhaseRef.current === "domain") {
      transitionTo("branding");
    }
    prevPhaseRef.current = phase;
  }, [hasDomainConnected]);

  useEffect(() => {
    if (newApiKeyToken) {
      setShowApiKeyModal(true);
    }
  }, [newApiKeyToken]);

  const transitionTo = useCallback((next: OnboardingPhase) => {
    setTransitioning(true);
    setTimeout(() => {
      setPhase(next);
      setTimeout(() => setTransitioning(false), 50);
    }, 300);
  }, []);

  const handleDomainConnected = useCallback(() => {
    transitionTo("branding");
  }, [transitionTo]);

  const handlePostClick = useCallback(
    (branding: BrandingDraftPayload) => {
      if (existingApiKey || newApiKeyToken) {
        setShowApiKeyModal(true);
      } else {
        if (!domain) return;

        router.post(
          continueOnboardingBrandingPath(),
          {
            domain_id: domain.id,
            bg_color_hex: branding.bgColorHex,
            text_color_hex: branding.textColorHex,
            selected_logo_url: branding.customLogo ? "" : branding.selectedLogoUrl || "",
            custom_logo: branding.customLogo,
            og_title: branding.ogTitle,
            og_description: branding.ogDescription,
            hero_image_url: branding.heroImageUrl,
          },
          {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => {
              router.post(
                "/onboarding/api_key",
                {},
                {
                  preserveScroll: true,
                  onSuccess: () => {
                    setShowApiKeyModal(true);
                  },
                },
              );
            },
          },
        );
      }
    },
    [domain, existingApiKey, newApiKeyToken],
  );

  const handleCompleteOnboarding = useCallback(() => {
    router.post("/onboarding/complete");
  }, []);

  const primaryDomain = domains[0];

  return (
    <>
      <Head title="Setup" />
      <div className="min-h-screen bg-gradient-to-br from-orange-50/80 via-white to-orange-100/40 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
        <FreeTrialBanner />

        <div className="mx-auto flex min-h-[calc(100vh-40px)] max-w-6xl flex-col px-3 py-5 sm:px-6 sm:py-8 lg:px-8">
          {/* Logo */}
          <div className="mb-6 flex justify-center sm:mb-10">
            <div className="flex items-center gap-2.5 sm:gap-3">
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-orange-500 sm:size-10 sm:rounded-xl">
                <AppLogoIcon className="size-5 text-white sm:size-6" />
              </div>
              <span className="text-xl font-bold text-orange-600 sm:text-2xl">OG Pilot</span>
            </div>
          </div>

          {/* Step content with transitions */}
          <div className="flex flex-1 items-start justify-center">
            <div
              className={`w-full transition-all duration-300 ease-in-out ${
                transitioning ? "translate-y-2 opacity-0" : "translate-y-0 opacity-100"
              }`}
            >
              {phase === "domain" && (
                <DomainStep
                  domain={domain}
                  claim={claim}
                  leadDomain={leadDomain}
                  onDomainConnected={handleDomainConnected}
                />
              )}

              {phase === "branding" && (
                <BrandingStep domain={domain} extraction={extraction} onPostClick={handlePostClick} />
              )}
            </div>
          </div>
        </div>
      </div>

      <ApiKeyModal
        open={showApiKeyModal}
        onOpenChange={setShowApiKeyModal}
        newApiKeyToken={newApiKeyToken}
        existingApiKey={existingApiKey}
        primaryDomain={primaryDomain}
        onComplete={handleCompleteOnboarding}
      />

      <Toaster richColors />
    </>
  );
}
